import {
  Button,
  Col,
  DatePicker,
  Form,
  Layout,
  Row,
  Table,
  Typography,
} from "antd";
import { ColumnsType } from "antd/es/table";
import dayjs from "dayjs";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useQuery } from "react-query";
import { useNavigate } from "react-router-dom";

import { appInject } from "@core/di/utils";
import { DI_TOKENS } from "@shared/constants/di";
import { ICampaignsService } from "@shared/interfaces/campaigns-service.interface";
import { DailyAffiliateRevenueRowDto } from "@shared/types/campaigns/campaign.dto";
import { ROUTES } from "@routes/routes";
import styles from "./DailyAffiliateRevenue.module.scss";

const { RangePicker } = DatePicker;

const DailyAffiliateRevenue = () => {
  const navigate = useNavigate();
  const campaignsService = appInject<ICampaignsService>(
    DI_TOKENS.campaignsService,
  );

  const [revenueRange, setRevenueRange] = useState<[dayjs.Dayjs, dayjs.Dayjs]>([
    dayjs().subtract(7, "day"),
    dayjs(),
  ]);
  const [loadReport, setLoadReport] = useState(false);
  const [containerHeight, setContainerHeight] = useState(0);
  const containerRef = useRef<HTMLDivElement>(null);

  const { data: dailyRevenue = [], isFetching } = useQuery(
    ["daily-affiliate-revenue", revenueRange[0], revenueRange[1]],
    () =>
      campaignsService.getDailyAffiliateRevenue({
        from: revenueRange[0].startOf("day").toISOString(),
        to: revenueRange[1].endOf("day").toISOString(),
      }),
    { enabled: loadReport },
  );

  const viewRedemptions = useCallback(
    (record: DailyAffiliateRevenueRowDto) => {
      const day = dayjs(record.day).format("YYYY-MM-DD");
      const params = new URLSearchParams({
        campaignId: record.campaignId,
        status: "used",
        redeemedFrom: day,
        redeemedTo: day,
        withCpaRedemption: "true",
      });
      navigate(`${ROUTES.CAMPAIGN_PROMO_CODES}?${params.toString()}`);
    },
    [navigate],
  );

  const columns: ColumnsType<DailyAffiliateRevenueRowDto> = useMemo(
    () => [
      { title: "Campaign", dataIndex: "campaignName", key: "campaignName" },
      {
        title: "Day (PT)",
        dataIndex: "day",
        key: "day",
        render: (v: string) => dayjs(v).format("YYYY-MM-DD"),
      },
      {
        title: "Redemptions",
        dataIndex: "redemptionCount",
        key: "redemptionCount",
      },
      {
        title: "Revenue (USD)",
        dataIndex: "totalRevenueUsd",
        key: "totalRevenueUsd",
        render: (v: number) => `$${v.toFixed(2)}`,
      },
      {
        title: "",
        key: "actions",
        width: 160,
        align: "right",
        render: (_, record) => (
          <Button
            type="primary"
            size="small"
            className={styles.viewRedemptionsBtn}
            onClick={() => viewRedemptions(record)}
          >
            View redemptions
          </Button>
        ),
      },
    ],
    [viewRedemptions],
  );

  useEffect(() => {
    const timer = setTimeout(() => {
      if (containerRef.current) {
        setContainerHeight(containerRef.current.offsetHeight - 140);
      }
    }, 100);

    return () => clearTimeout(timer);
  }, [dailyRevenue.length, loadReport]);

  return (
    <Layout className={styles.tableLayout}>
      <Row
        style={{
          height: 32,
          justifyContent: "space-between",
          alignItems: "flex-start",
        }}
      >
        <Col
          span={12}
          style={{
            display: "flex",
            alignItems: "center",
            rowGap: 5,
            columnGap: 5,
          }}
        >
          <Typography.Title style={{ margin: 0 }} level={5}>
            Daily revenue
          </Typography.Title>
        </Col>
        <Col>
          <Form.Item className="search-input-container">
            <RangePicker
              value={revenueRange}
              onChange={(dates) => {
                if (dates?.[0] && dates[1]) {
                  setRevenueRange([dates[0], dates[1]]);
                  setLoadReport(false);
                }
              }}
            />
            <Button
              type="primary"
              loading={isFetching}
              onClick={() => setLoadReport(true)}
            >
              Load report
            </Button>
          </Form.Item>
        </Col>
      </Row>

      <Layout.Content
        ref={containerRef}
        className={styles.content}
        style={{ backgroundColor: "white" }}
      >
        <Table
          rowKey={(r) => `${r.campaignId}-${r.day}`}
          loading={isFetching}
          columns={columns}
          dataSource={dailyRevenue}
          pagination={{ pageSize: 50, showSizeChanger: true }}
          size="small"
          scroll={{ y: containerHeight }}
        />
      </Layout.Content>
    </Layout>
  );
};

export default DailyAffiliateRevenue;
